import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const HumanBody = () => {
  const navigate = useNavigate();
  const [hoveredPart, setHoveredPart] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Smooth mouse tracking - disabled on mobile
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { damping: 20, stiffness: 100 });
  const smoothY = useSpring(mouseY, { damping: 20, stiffness: 100 });

  useEffect(() => {
    if (isMobile) return; // Skip mouse tracking on mobile

    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        mouseX.set(x * 0.05);
        mouseY.set(y * 0.05);
        setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, [mouseX, mouseY, isMobile]);

  // Network Graph Layout - Professional Design with Symmetric Distribution
  const bodyParts = [
    // Center Hub - Main Project (Cardio in center)
    { 
      id: 'cardio', 
      name: 'Cardio', 
      description: 'Cardiovascular',
      color: '#E91E63',
      cx: 280,
      cy: 260,
      size: 'large',
      path: '/projects/cardio',
      organPaths: [
        `M280,240 C280,240 265,228 257,236 C250,244 250,256 257,264 L280,282 L303,264 C310,256 310,244 303,236 C295,228 280,240 280,240 Z`,
        `M270,250 L277,257 L284,245`,
        `M290,250 L283,257 L276,245`
      ]
    },
    // Inner Ring - 6 main projects (60° apart)
    { 
      id: 'neuro', 
      name: 'Neuro', 
      description: 'Neurological',
      color: '#9C27B0',
      cx: 280,
      cy: 120,
      size: 'medium',
      path: '/projects/neuro',
      connections: ['cardio'],
      organPaths: [
        `M280,102 Q260,99 245,112 Q240,125 245,138 Q254,150 280,150 Q306,150 315,138 Q320,125 315,112 Q300,99 280,102 Z`,
        `M270,120 Q266,124 270,129 Q274,134 280,129 Q286,124 280,120 Q274,115 270,120 Z`,
        `M290,120 Q286,124 290,129 Q294,134 300,129 Q306,124 300,120 Q294,115 290,120 Z`
      ]
    },
    { 
      id: 'vision', 
      name: 'Vision', 
      description: 'Retinal',
      color: '#00BCD4',
      cx: 401,
      cy: 190,
      size: 'medium',
      path: '/projects/vision',
      connections: ['cardio'],
      organPaths: [
        `M386,178 Q381,182 386,187 Q391,193 398,187 Q403,182 398,178 Q391,172 386,178 Z`,
        `M404,178 Q399,182 404,187 Q409,193 416,187 Q421,182 416,178 Q409,172 404,178 Z`,
        `M392,182 A2,2 0 1,1 392,182.1 Z`,
        `M410,182 A2,2 0 1,1 410,182.1 Z`
      ]
    },
    { 
      id: 'pulmo', 
      name: 'Pulmo', 
      description: 'Respiratory',
      color: '#00BCD4',
      cx: 401,
      cy: 330,
      size: 'medium',
      path: '/projects/pulmo',
      connections: ['cardio'],
      organPaths: [
        `M381,315 Q371,315 371,327 Q371,347 381,352 Q385,354 389,352 L389,327 Q389,319 381,315 Z`,
        `M421,315 Q431,315 431,327 Q431,347 421,352 Q417,354 413,352 L413,327 Q413,319 421,315 Z`,
        `M383,322 L387,322 M383,330 L387,330 M383,338 L387,338`,
        `M415,322 L419,322 M415,330 L419,330 M415,338 L419,338`
      ]
    },
    { 
      id: 'nutriscan', 
      name: 'NutriScan', 
      description: 'Metabolic',
      color: '#4CAF50',
      cx: 280,
      cy: 400,
      size: 'medium',
      path: '/projects/nutriscan',
      connections: ['cardio'],
      organPaths: [
        `M280,380 Q252,380 252,400 Q252,420 280,420 Q308,420 308,400 Q308,380 280,380 Z`,
        `M265,400 Q265,389 280,389 Q295,389 295,400`,
        `M270,408 Q280,412 290,408`
      ]
    },
    { 
      id: 'derma', 
      name: 'Derma', 
      description: 'Dermatological',
      color: '#9C27B0',
      cx: 159,
      cy: 330,
      size: 'medium',
      path: '/projects/derma',
      connections: ['cardio'],
      organPaths: [
        `M144,315 Q140,315 140,319 L140,341 Q140,345 144,345 L159,345 Q163,345 163,341 L163,319 Q163,315 159,315 Z`,
        `M167,315 Q163,315 163,319 L163,341 Q163,345 167,345 L182,345 Q186,345 186,341 L186,319 Q186,315 182,315 Z`,
        `M146,322 L157,322 M146,330 L157,330 M146,338 L157,338`,
        `M169,322 L180,322 M169,330 L180,330 M169,338 L180,338`
      ]
    },
    { 
      id: 'elderband', 
      name: 'ElderBand', 
      description: 'Safety',
      color: '#06B6D4',
      cx: 159,
      cy: 190,
      size: 'medium',
      path: '/projects/elderband',
      connections: ['cardio'],
      organPaths: [
        `M144,177 Q140,177 140,181 L140,199 Q140,203 144,203 L174,203 Q178,203 178,199 L178,181 Q178,177 174,177 Z`,
        `M148,184 L156,184 M148,188 L156,188 M148,192 L156,192 M148,196 L156,196`,
        `M162,184 L170,184 M162,188 L170,188 M162,192 L170,192 M162,196 L170,196`
      ]
    },
    // Outer Ring - 6 satellite projects (60° apart)
    { 
      id: 'fallcam', 
      name: 'FallCam', 
      description: 'Fall Detection',
      color: '#F97316',
      cx: 441,
      cy: 100,
      size: 'small',
      path: '/projects/fallcam',
      connections: ['neuro', 'vision'],
      organPaths: [
        `M429,88 L429,112 L453,112 L453,88 Z`,
        `M434,93 L434,107 M441,93 L441,107 M448,93 L448,107`,
        `M431,100 L451,100`
      ]
    },
    { 
      id: 'heartpatch', 
      name: 'HeartPatch', 
      description: 'ECG',
      color: '#EF4444',
      cx: 491,
      cy: 260,
      size: 'small',
      path: '/projects/heartpatch',
      connections: ['vision', 'pulmo'],
      organPaths: [
        `M481,251 Q477,255 481,259 Q485,263 490,259 Q494,255 490,251 Q485,247 481,251 Z`,
        `M495,251 Q491,255 495,259 Q499,263 504,259 Q508,255 504,251 Q499,247 495,251 Z`,
        `M485,255 L486,258 L488,252 L490,258 L492,255 L498,255 L499,258 L501,252 L503,258 L504,255`
      ]
    },
    { 
      id: 'sleepband', 
      name: 'SleepBand', 
      description: 'Sleep',
      color: '#6366F1',
      cx: 441,
      cy: 420,
      size: 'small',
      path: '/projects/sleepband',
      connections: ['pulmo', 'nutriscan'],
      organPaths: [
        `M431,411 Q427,415 431,419 Q435,423 439,419 L448,419 Q452,423 456,419 Q460,415 456,411 L431,411 Z`,
        `M438,415 A3,3 0 1,1 438,415.15 Z`,
        `M448,415 A3,3 0 1,1 448,415.15 Z`
      ]
    },
    { 
      id: 'skincam', 
      name: 'SkinCam', 
      description: 'Skin',
      color: '#D946EF',
      cx: 69,
      cy: 260,
      size: 'small',
      path: '/projects/skincam',
      connections: ['elderband', 'derma'],
      organPaths: [
        `M59,251 Q55,255 59,259 Q63,263 68,259 Q72,255 68,251 Q63,247 59,251 Z`,
        `M73,251 Q69,255 73,259 Q77,263 82,259 Q86,255 82,251 Q77,247 73,251 Z`,
        `M63,255 L64,258 L66,252 L68,258 L70,255 L76,255 L77,258 L79,252 L81,258 L82,255`
      ]
    },
    { 
      id: 'healthnet', 
      name: 'HealthNet', 
      description: 'Platform',
      color: '#F59E0B',
      cx: 119,
      cy: 420,
      size: 'small',
      path: '/projects/healthnet',
      connections: ['derma', 'nutriscan'],
      organPaths: [
        `M119,408 L105,416 L105,428 L119,436 L133,428 L133,416 Z`,
        `M114,419 L119,422 L124,419`,
        `M114,425 L119,422 L124,425`
      ]
    },
    { 
      id: 'cardio2', 
      name: 'MediHub', 
      description: 'Integration',
      color: '#00BCD4',
      cx: 119,
      cy: 100,
      size: 'small',
      path: '/projects/cardio',
      connections: ['elderband', 'neuro'],
      organPaths: [
        `M111,92 L111,108 L127,108 L127,92 Z`,
        `M114,96 L124,96 M114,100 L124,100 M114,104 L124,104`,
        `M117,98 L121,98 M117,102 L121,102`
      ]
    }
  ];

  const handleClick = (path) => navigate(path);

  // Calculate responsive sizes and positions for mobile
  const getResponsiveRadius = (size) => {
    const baseRadius = size === 'large' ? 55 : size === 'medium' ? 42 : 32;
    return isMobile ? baseRadius * 0.75 : baseRadius; // 25% smaller on mobile
  };

  const getResponsiveFontSize = (size) => {
    const baseFontSize = size === 'large' ? 14 : size === 'medium' ? 12 : 10;
    return isMobile ? baseFontSize * 0.85 : baseFontSize; // Slightly smaller text on mobile
  };

  const getResponsiveViewBox = () => {
    return isMobile ? "0 0 560 520" : "0 0 560 520"; // Same viewBox
  };

  const getResponsiveScale = () => {
    return isMobile ? 0.95 : 1; // Slightly smaller overall on mobile
  };

  return (
    <div 
      ref={containerRef} 
      className="relative w-full mx-auto select-none overflow-visible"
      style={{
        maxWidth: isMobile ? '100%' : '672px',
        padding: isMobile ? '1rem 0.5rem' : '3rem 0'
      }}
    >
      <motion.svg 
        viewBox={getResponsiveViewBox()}
        className="w-full h-auto drop-shadow-2xl" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ 
          x: isMobile ? 0 : smoothX, 
          y: isMobile ? 0 : smoothY,
          filter: 'drop-shadow(0 25px 50px rgba(0, 188, 212, 0.15))',
          transform: `scale(${getResponsiveScale()})`
        }}
      >
        <defs>
          {/* Professional Glow Filter */}
          <filter id="professionalGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur"/>
            <feColorMatrix in="blur" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 25 -10" result="glow"/>
            <feBlend in="SourceGraphic" in2="glow"/>
          </filter>

          {/* Inner Shadow for Depth */}
          <filter id="innerShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur"/>
            <feOffset in="blur" dx="0" dy="2" result="offsetBlur"/>
            <feComposite in="offsetBlur" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="innerShadow"/>
            <feColorMatrix in="innerShadow" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.3 0"/>
            <feBlend in="SourceGraphic" in2="innerShadow" mode="normal"/>
          </filter>

          {/* Radial Gradients for 3D effect */}
          <radialGradient id="brainGradient3D" cx="40%" cy="30%">
            <stop offset="0%" style={{ stopColor: '#BA68C8', stopOpacity: 1 }}/>
            <stop offset="50%" style={{ stopColor: '#9C27B0', stopOpacity: 0.9 }}/>
            <stop offset="100%" style={{ stopColor: '#6A1B9A', stopOpacity: 0.6 }}/>
          </radialGradient>

          <radialGradient id="cyanGradient3D" cx="40%" cy="30%">
            <stop offset="0%" style={{ stopColor: '#4DD0E1', stopOpacity: 1 }}/>
            <stop offset="50%" style={{ stopColor: '#00BCD4', stopOpacity: 0.9 }}/>
            <stop offset="100%" style={{ stopColor: '#0097A7', stopOpacity: 0.6 }}/>
          </radialGradient>

          {/* Glass morphism effect */}
          <filter id="glassMorphism" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2"/>
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.4 0"/>
          </filter>

          {/* Neural Fabric Pattern for cloth texture */}
          <pattern id="neuralFabric" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M0,10 L10,0 M10,20 L20,10" stroke="#00BCD4" strokeWidth="0.5" opacity="0.3"/>
            <circle cx="5" cy="5" r="1" fill="#00BCD4" opacity="0.2"/>
            <circle cx="15" cy="15" r="1" fill="#9C27B0" opacity="0.2"/>
          </pattern>

          {/* Digital Cloth Shader */}
          <linearGradient id="digitalCloth" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#0B1220', stopOpacity: 0.95 }}>
              <animate attributeName="stop-opacity" values="0.95;0.85;0.95" dur="3s" repeatCount="indefinite"/>
            </stop>
            <stop offset="50%" style={{ stopColor: '#00BCD4', stopOpacity: 0.15 }}/>
            <stop offset="100%" style={{ stopColor: '#9C27B0', stopOpacity: 0.95 }}>
              <animate attributeName="stop-opacity" values="0.95;0.85;0.95" dur="3s" repeatCount="indefinite" begin="1.5s"/>
            </stop>
          </linearGradient>

          {/* Reveal Mask Effect */}
          <mask id="revealMask">
            <circle cx="140" cy="250" r="300" fill="white"/>
          </mask>

          {/* Glow for data overlays */}
          <filter id="dataGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Connection Lines - Network Style */}
        <g opacity="0.3">
          {bodyParts.map((part) => 
            part.connections?.map((targetId) => {
              const target = bodyParts.find(p => p.id === targetId);
              if (!target) return null;
              return (
                <motion.line
                  key={`${part.id}-${targetId}`}
                  x1={part.cx}
                  y1={part.cy}
                  x2={target.cx}
                  y2={target.cy}
                  stroke="#00BCD4"
                  strokeWidth="1"
                  strokeDasharray="4,4"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ 
                    pathLength: 1, 
                    opacity: hoveredPart === part.id || hoveredPart === targetId ? 0.6 : 0.2
                  }}
                  transition={{ duration: 1.5, ease: "easeInOut" }}
                />
              );
            })
          )}
        </g>

        {/* Project Circles in Network Graph Layout */}
        {bodyParts.map((part) => {
          const isHovered = hoveredPart === part.id;
          const gradient = part.color === '#9C27B0' ? 'url(#brainGradient3D)' : 'url(#cyanGradient3D)';
          const radius = getResponsiveRadius(part.size);
          const fontSize = getResponsiveFontSize(part.size);
          
          return (
            <motion.g 
              key={part.id}
              onMouseEnter={() => !isMobile && setHoveredPart(part.id)}
              onMouseLeave={() => !isMobile && setHoveredPart(null)}
              onClick={() => handleClick(part.path)}
              className="cursor-pointer"
              whileHover={!isMobile ? { scale: 1.12 } : {}}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              style={{ transformOrigin: `${part.cx}px ${part.cy}px` }}
            >
              {/* Outer glow rings on hover */}
              {isHovered && [radius + 5, radius + 10, radius + 15].map((r, i) => (
                <motion.circle
                  key={`ring-${i}`}
                  cx={part.cx}
                  cy={part.cy}
                  r={r}
                  fill="none"
                  stroke={part.color}
                  strokeWidth="0.5"
                  opacity="0"
                  animate={{ 
                    scale: [1, 1.3, 1.5],
                    opacity: [0.5, 0.2, 0]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: "easeOut"
                  }}
                />
              ))}

              {/* Main glass circle - brand cyan border */}
              <motion.circle
                cx={part.cx}
                cy={part.cy}
                r={radius}
                fill="url(#digitalCloth)"
                stroke="#00BCD4"
                strokeWidth={isHovered ? "2.5" : part.size === 'large' ? "2" : "1.5"}
                opacity={0.95}
                filter={isHovered ? "url(#professionalGlow)" : "none"}
                animate={isHovered ? {
                  opacity: [0.95, 1, 0.95]
                } : {}}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              {/* Inner decorative circles - brand cyan */}
              {!isHovered && [radius - 10, radius - 15, radius - 20].map((r, i) => (
                r > 5 && (
                  <motion.circle
                    key={`inner-${i}`}
                    cx={part.cx}
                    cy={part.cy}
                    r={r}
                    fill="none"
                    stroke="#00BCD4"
                    strokeWidth="0.5"
                    opacity="0.15"
                    strokeDasharray="3,3"
                    animate={{
                      rotate: [0, 360],
                      opacity: [0.1, 0.25, 0.1]
                    }}
                    transition={{
                      rotate: { duration: 15 + i * 5, repeat: Infinity, ease: "linear" },
                      opacity: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                    }}
                  />
                )
              ))}

              {/* Project name - brand cyan */}
              <motion.text 
                x={part.cx} 
                y={part.cy + 4} 
                textAnchor="middle" 
                fill="#00BCD4"
                fontSize={fontSize}
                fontWeight="700"
                fontFamily="Poppins, sans-serif"
                opacity={isHovered ? 0 : 1}
                style={{ 
                  textShadow: '0 0 15px rgba(0, 188, 212, 0.5)',
                  letterSpacing: '0.2px'
                }}
              >
                {part.name}
              </motion.text>

              {/* Project icon/shape on hover */}
              {isHovered && part.organPaths && (
                <motion.g
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, ease: "backOut" }}
                >
                  {part.organPaths.map((path, index) => (
                    <motion.path
                      key={index}
                      d={path}
                      fill={index === 0 ? gradient : 'none'}
                      stroke={part.color}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ 
                        duration: 0.8, 
                        delay: index * 0.15,
                        ease: "easeInOut"
                      }}
                    />
                  ))}
                </motion.g>
              )}

              {/* Project name on hover - with project color */}
              {isHovered && (
                <motion.text
                  x={part.cx}
                  y={part.cy + radius + 18}
                  textAnchor="middle"
                  fill={part.color}
                  fontSize={fontSize + 1}
                  fontWeight="700"
                  fontFamily="Poppins, sans-serif"
                  initial={{ opacity: 0, y: part.cy + radius + 13 }}
                  animate={{ opacity: 1, y: part.cy + radius + 18 }}
                  transition={{ duration: 0.3 }}
                  style={{ 
                    textShadow: `0 0 10px ${part.color}`,
                  }}
                >
                  {part.name}
                </motion.text>
              )}

              {/* Click indicator - larger for large circles */}
              {isHovered && (
                <motion.g
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 20 }}
                >
                  <circle 
                    cx={part.cx + (radius * 0.5)} 
                    cy={part.cy - (radius * 0.5)} 
                    r={part.size === 'large' ? 10 : 8} 
                    fill={part.color} 
                    opacity="0.9"
                  />
                  <path 
                    d={`M${part.cx + (radius * 0.5) - 3},${part.cy - (radius * 0.5)} L${part.cx + (radius * 0.5) - 1},${part.cy - (radius * 0.5) + 2} L${part.cx + (radius * 0.5) + 3},${part.cy - (radius * 0.5) - 2}`}
                    stroke="white" 
                    strokeWidth="1.5" 
                    fill="none" 
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.g>
              )}
            </motion.g>
          );
        })}
      </motion.svg>

      {/* Hover Tooltip with Animation */}
      {hoveredPart && (
        <motion.div 
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.8 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 20,
            mass: 0.5
          }}
          className="absolute left-1/2 -translate-x-1/2 bottom-0 mb-4 pointer-events-none z-50"
        >
          <motion.div 
            className="glass-effect rounded-2xl p-6 shadow-2xl border-2 min-w-[320px]"
            style={{ 
              borderColor: bodyParts.find(p => p.id === hoveredPart)?.color,
              boxShadow: `0 0 40px ${bodyParts.find(p => p.id === hoveredPart)?.color}50, 0 20px 60px rgba(0,0,0,0.5)`
            }}
            animate={{
              boxShadow: [
                `0 0 40px ${bodyParts.find(p => p.id === hoveredPart)?.color}50`,
                `0 0 60px ${bodyParts.find(p => p.id === hoveredPart)?.color}70`,
                `0 0 40px ${bodyParts.find(p => p.id === hoveredPart)?.color}50`
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="text-center">
              <motion.h4 
                className="text-2xl font-bold mb-2"
                style={{ color: bodyParts.find(p => p.id === hoveredPart)?.color }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                Yamai {bodyParts.find(p => p.id === hoveredPart)?.name}
              </motion.h4>
              <p className="text-[#8A90A0] text-sm mb-4 leading-relaxed">
                {bodyParts.find(p => p.id === hoveredPart)?.description}
              </p>
              <motion.div 
                className="flex items-center justify-center gap-2 text-white"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <span className="text-xs font-medium">Click to explore</span>
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path 
                    d="M8 2L14 8L8 14M14 8H2" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Advanced Mouse Follower Effect - Desktop Only */}
      {!isMobile && (
        <motion.div
          className="absolute pointer-events-none rounded-full z-40"
          animate={{
            left: mousePosition.x - 15,
            top: mousePosition.y - 15,
          }}
          transition={{ type: "spring", damping: 30, stiffness: 200, mass: 0.5 }}
        >
          {/* Outer glow ring */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              width: 30,
              height: 30,
              background: hoveredPart 
                ? `radial-gradient(circle, ${bodyParts.find(p => p.id === hoveredPart)?.color}40 0%, transparent 70%)`
                : 'radial-gradient(circle, rgba(0, 188, 212, 0.2) 0%, transparent 70%)',
            }}
            animate={{
              scale: hoveredPart ? [1, 1.5, 1] : 1,
              opacity: hoveredPart ? [0.5, 0.8, 0.5] : 0.3,
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          
          {/* Inner dot */}
          <motion.div
            className="absolute rounded-full"
            style={{
              width: 8,
              height: 8,
              left: '50%',
              top: '50%',
              x: '-50%',
              y: '-50%',
              background: hoveredPart 
                ? bodyParts.find(p => p.id === hoveredPart)?.color
                : '#00BCD4',
              boxShadow: hoveredPart 
                ? `0 0 20px ${bodyParts.find(p => p.id === hoveredPart)?.color}`
                : '0 0 20px #00BCD4',
            }}
            animate={{
              scale: hoveredPart ? [1, 1.3, 1] : 1,
            }}
            transition={{ duration: 1, repeat: Infinity }}
          />
        </motion.div>
      )}

      {/* Floating particles around cursor when hovering - Desktop Only */}
      {!isMobile && hoveredPart && (
        <>
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute pointer-events-none rounded-full"
              style={{
                width: 4,
                height: 4,
                background: bodyParts.find(p => p.id === hoveredPart)?.color,
                left: mousePosition.x,
                top: mousePosition.y,
              }}
              animate={{
                x: Math.cos((i * Math.PI * 2) / 8) * 40,
                y: Math.sin((i * Math.PI * 2) / 8) * 40,
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.1,
                ease: "easeOut"
              }}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default HumanBody;
